import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useCalculos } from "@/hooks/useCalculos";
import { CalculoTrabalhistaResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const CalculosTrabalhistas = () => {
  const [salario, setSalario] = useState("");
  const [diasTrabalhados, setDiasTrabalhados] = useState("");
  const [mesesTrabalhados, setMesesTrabalhados] = useState("");
  const [temFerias, setTemFerias] = useState(true);
  const [tem13, setTem13] = useState(true);
  const [resultado, setResultado] = useState<CalculoTrabalhistaResult | null>(null);
  
  const { calcularTrabalhista, loading } = useCalculos();

  useCalculoPrefill("trabalhistas", (v) => {
    setSalario(v.salario ?? "");
    setDiasTrabalhados(v.diasTrabalhados ?? "");
    setMesesTrabalhados(v.mesesTrabalhados ?? "");
    setTemFerias(v.temFerias ?? true);
    setTem13(v.tem13 ?? true);
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!salario || !diasTrabalhados || !mesesTrabalhados) {
      return;
    }

    try {
      const result = await calcularTrabalhista({
        salario: parseFloat(salario),
        diasTrabalhados: parseInt(diasTrabalhados),
        mesesTrabalhados: parseInt(mesesTrabalhados),
        temFerias,
        tem13
      });
      setResultado(result);
      addToHistory({
        type: "trabalhistas",
        label: "Verbas Trabalhistas",
        values: { salario, diasTrabalhados, mesesTrabalhados, temFerias, tem13 },
        summary: `Salário ${formatCurrency(parseFloat(salario))} • Total ${formatCurrency(result.total)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cálculos Trabalhistas</CardTitle>
        <p className="text-sm text-muted-foreground">
          Verbas rescisórias: saldo salário, férias, 13º salário e multa FGTS
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="salario">Salário Mensal</Label>
            <Input
              id="salario"
              type="number"
              placeholder="1500.00"
              value={salario}
              onChange={(e) => setSalario(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="dias-trabalhados">Dias Trabalhados no Mês</Label>
            <Input
              id="dias-trabalhados"
              type="number"
              placeholder="20"
              value={diasTrabalhados}
              onChange={(e) => setDiasTrabalhados(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="meses-trabalhados">Meses Trabalhados</Label>
            <Input
              id="meses-trabalhados"
              type="number"
              placeholder="12"
              value={mesesTrabalhados}
              onChange={(e) => setMesesTrabalhados(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="ferias" 
                checked={temFerias}
                onCheckedChange={(checked) => setTemFerias(checked as boolean)}
              />
              <Label htmlFor="ferias">Tem direito a férias</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="decimo" 
                checked={tem13}
                onCheckedChange={(checked) => setTem13(checked as boolean)}
              />
              <Label htmlFor="decimo">Tem direito ao 13º</Label>
            </div>
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Verbas Trabalhistas"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Verbas Rescisórias:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Saldo de Salário:</strong> {formatCurrency(resultado.saldoSalario)}</p>
                <p><strong>Férias + 1/3:</strong> {formatCurrency(resultado.ferias)}</p>
                <p><strong>13º Salário:</strong> {formatCurrency(resultado.decimoTerceiro)}</p>
              </div>
              <div>
                <p><strong>Multa FGTS (40%):</strong> {formatCurrency(resultado.multaFGTS)}</p>
                <p className="text-green-600 text-lg"><strong>Total:</strong> {formatCurrency(resultado.total)}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};